// Pre-auth workspace registration.
//
// Reachable at /register-workspace from the login screen. Runs three panes:
//   1. 'form'     — the full workspace + admin form (single page; the "Public
//                    Site" step from the reference design was dropped)
//   2. 'otp'      — user enters the 6-digit code sent to the admin email.
//                    Verifying it triggers the atomic clinic-registration
//                    endpoint (creates clinician + clinic + admin membership
//                    + mints tokens in one call).
//   3. 'success'  — brief confirmation before we signIn() and let App.tsx
//                    render the authed tree.
//
// The workspace phone is still captured (used to phone-key the clinician
// row so mobile OTP login continues to work), but it is not verified during
// registration — email is the only proof-of-ownership check up front.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CLINIC_FACILITY_TYPES,
    CLINIC_FACILITY_TYPE_LABELS,
    NIGERIAN_STATES,
    lgasForState,
    type ClinicFacilityType,
} from '@pamosi/shared';
import { authApi, ApiError } from '../lib/api';
import { getOrCreateDeviceUuid } from '../lib/storage';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Nigeria-only for v1. Country dropdown is single-value so it renders the
// same shape as the reference design (a real dropdown) without pretending
// we support any other locale yet.
const COUNTRY_OPTIONS: readonly { code: string; label: string }[] = [
    { code: 'NG', label: 'Nigeria' },
];

// Full form state kept in one object so the form → otp handoff can simply
// pass a reference instead of destructuring twenty fields.
interface FormState {
    // Workspace
    facilityType: ClinicFacilityType | '';
    licenseNumber: string;
    facilityName: string;
    workspaceEmail: string;
    workspacePhone: string;
    physicalAddress: string;
    country: string;
    state: string;
    lga: string;
    // Admin
    fullName: string;
    adminEmail: string;
    username: string;
    password: string;
    confirmPassword: string;
}

const EMPTY_FORM: FormState = {
    facilityType: '',
    licenseNumber: '',
    facilityName: '',
    workspaceEmail: '',
    workspacePhone: '',
    physicalAddress: '',
    country: 'NG',
    state: '',
    lga: '',
    fullName: '',
    adminEmail: '',
    username: '',
    password: '',
    confirmPassword: '',
};

type Pane =
    | { kind: 'form' }
    | { kind: 'otp'; form: FormState }
    | { kind: 'success' };

export function RegisterWorkspaceScreen() {
    const [pane, setPane] = useState<Pane>({ kind: 'form' });
    const navigate = useNavigate();

    return (
        <div className="min-h-full flex flex-col bg-slate-50">
            <header className="px-6 py-4 flex items-center justify-between">
                <Logo size={32} />
                <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                >
                    ← Back to sign in
                </button>
            </header>
            <div className="flex-1 flex items-start justify-center p-4">
                <div className="pm-card w-full max-w-2xl p-6 md:p-8">
                    <div className="text-brand-700 text-xs uppercase tracking-wide font-semibold">
                        Pamosi Clinic
                    </div>
                    <h1 className="text-slate-900 text-2xl font-semibold mt-1">
                        Register Workspace
                    </h1>
                    {pane.kind === 'form' && (
                        <FormPane
                            onSubmit={(form) => setPane({ kind: 'otp', form })}
                        />
                    )}
                    {pane.kind === 'otp' && (
                        <OtpPane
                            form={pane.form}
                            onBack={() => setPane({ kind: 'form' })}
                            onSuccess={() => setPane({ kind: 'success' })}
                        />
                    )}
                    {pane.kind === 'success' && <SuccessPane />}
                </div>
            </div>
        </div>
    );
}

// ─── Form pane ──────────────────────────────────────────────────────────

function FormPane({ onSubmit }: { onSubmit: (form: FormState) => void }) {
    const [f, setF] = useState<FormState>(EMPTY_FORM);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // LGA options depend on the currently-selected state. Recomputes when
    // the user picks a state; clears the picked LGA if it no longer belongs.
    const lgaOptions = useMemo(() => lgasForState(f.state), [f.state]);

    function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
        setF((prev) => {
            const next = { ...prev, [key]: value };
            // If they switched states, wipe the LGA — the old value almost
            // certainly isn't valid in the new state.
            if (key === 'state' && prev.state !== value) {
                next.lga = '';
            }
            return next;
        });
    }

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        const err = validate(f);
        if (err) {
            setError(err);
            return;
        }
        setBusy(true);
        try {
            // Fire the email OTP now, at the pane boundary, so the code
            // lands in the admin's inbox while they're still on this screen.
            await authApi.requestEmailOtp(f.adminEmail.trim().toLowerCase());
            onSubmit({ ...f });
        } catch (err) {
            setError(emailOtpRequestErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={submit} className="mt-6 space-y-8">
            {/* ─── Workspace Information ─────────────────────────────── */}
            <section>
                <SectionHeader
                    icon="🏥"
                    title="Workspace Information"
                    subtitle="Details about your clinic or hospital facility."
                />
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="pm-label" htmlFor="facility-type">
                            Facility Type <Req />
                        </label>
                        <select
                            id="facility-type"
                            className="pm-input"
                            value={f.facilityType}
                            onChange={(e) => patch('facilityType', e.target.value as ClinicFacilityType)}
                            disabled={busy}
                        >
                            <option value="">Select facility type</option>
                            {CLINIC_FACILITY_TYPES.map((t) => (
                                <option key={t} value={t}>{CLINIC_FACILITY_TYPE_LABELS[t]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="license">
                            License Number <Req />
                        </label>
                        <input
                            id="license"
                            className="pm-input"
                            placeholder="MDCN / practice licence"
                            value={f.licenseNumber}
                            onChange={(e) => patch('licenseNumber', e.target.value)}
                            disabled={busy}
                        />
                        <Hint>Official health-facility registration / operating licence.</Hint>
                    </div>
                    <div className="md:col-span-2">
                        <label className="pm-label" htmlFor="facility-name">
                            Workspace Name <Req />
                        </label>
                        <input
                            id="facility-name"
                            className="pm-input"
                            placeholder="e.g. Adebayo Family Clinic"
                            value={f.facilityName}
                            onChange={(e) => patch('facilityName', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="workspace-email">
                            Workspace Email <Req />
                        </label>
                        <input
                            id="workspace-email"
                            className="pm-input"
                            type="email"
                            placeholder="info@facility.com"
                            value={f.workspaceEmail}
                            onChange={(e) => patch('workspaceEmail', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="workspace-phone">
                            Workspace Phone <Req />
                        </label>
                        <input
                            id="workspace-phone"
                            className="pm-input"
                            type="tel"
                            placeholder="+2348012345678"
                            value={f.workspacePhone}
                            onChange={(e) => patch('workspacePhone', e.target.value.trim())}
                            disabled={busy}
                        />
                        <Hint>Also the admin phone — used to sign in on Pamosi Clinic mobile. Not verified here.</Hint>
                    </div>
                    <div className="md:col-span-2">
                        <label className="pm-label" htmlFor="address">
                            Physical Address <Req />
                        </label>
                        <input
                            id="address"
                            className="pm-input"
                            placeholder="e.g. 45 Broad Street, Lagos Island"
                            value={f.physicalAddress}
                            onChange={(e) => patch('physicalAddress', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="country">
                            Country <Req />
                        </label>
                        <select
                            id="country"
                            className="pm-input"
                            value={f.country}
                            onChange={(e) => patch('country', e.target.value)}
                            disabled={busy}
                        >
                            {COUNTRY_OPTIONS.map((o) => (
                                <option key={o.code} value={o.code}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="state">
                            State <Req />
                        </label>
                        <select
                            id="state"
                            className="pm-input"
                            value={f.state}
                            onChange={(e) => patch('state', e.target.value)}
                            disabled={busy}
                        >
                            <option value="">Select state</option>
                            {NIGERIAN_STATES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="lga">
                            LGA <Req />
                        </label>
                        <select
                            id="lga"
                            className="pm-input"
                            value={f.lga}
                            onChange={(e) => patch('lga', e.target.value)}
                            disabled={busy || !f.state}
                        >
                            <option value="">
                                {f.state ? 'Select LGA' : 'Pick state first'}
                            </option>
                            {lgaOptions.map((l) => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            {/* ─── Administrator Credentials ────────────────────────── */}
            <section>
                <SectionHeader
                    icon="👤"
                    title="Administrator Credentials"
                    subtitle="The admin becomes the workspace owner and can invite the rest of the team."
                />
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="pm-label" htmlFor="admin-name">
                            Admin Full Name <Req />
                        </label>
                        <input
                            id="admin-name"
                            className="pm-input"
                            placeholder="Dr. Adeyemi Okafor"
                            value={f.fullName}
                            onChange={(e) => patch('fullName', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="admin-email">
                            Admin Email <Req />
                        </label>
                        <input
                            id="admin-email"
                            className="pm-input"
                            type="email"
                            placeholder="adeyemi@lagosgeneral.com"
                            value={f.adminEmail}
                            onChange={(e) => patch('adminEmail', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="pm-label" htmlFor="admin-username">
                            Admin Username <Req />
                        </label>
                        <input
                            id="admin-username"
                            className="pm-input"
                            placeholder="Choose a username (3–30 chars)"
                            value={f.username}
                            onChange={(e) => patch('username', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="password">
                            Password <Req />
                        </label>
                        <input
                            id="password"
                            className="pm-input"
                            type="password"
                            placeholder="Min 8 characters"
                            value={f.password}
                            onChange={(e) => patch('password', e.target.value)}
                            disabled={busy}
                        />
                        <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                            <div>• Min 8 characters</div>
                            <div>• Contains a letter</div>
                            <div>• Contains a number</div>
                        </div>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="confirm">
                            Confirm Password <Req />
                        </label>
                        <input
                            id="confirm"
                            className="pm-input"
                            type="password"
                            placeholder="Re-enter password"
                            value={f.confirmPassword}
                            onChange={(e) => patch('confirmPassword', e.target.value)}
                            disabled={busy}
                        />
                    </div>
                </div>
            </section>

            {error && <div className="pm-error">{error}</div>}

            <div className="flex flex-col md:flex-row md:justify-end gap-3">
                <button
                    className="pm-btn-primary w-full md:w-auto md:px-6"
                    type="submit"
                    disabled={busy}
                >
                    {busy ? 'Sending verification code…' : 'Continue → Verify email'}
                </button>
            </div>
        </form>
    );
}

// ─── OTP pane ──────────────────────────────────────────────────────────

function OtpPane({
    form,
    onBack,
    onSuccess,
}: {
    form: FormState;
    onBack: () => void;
    onSuccess: () => void;
}) {
    const { signIn } = useAuth();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [resent, setResent] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        if (!/^\d{6}$/.test(code)) {
            setError('Enter the 6-digit code from your email.');
            return;
        }
        setBusy(true);
        try {
            // Single atomic call — server verifies the email code, creates
            // the clinician + clinic + admin membership, and mints tokens.
            const res = await authApi.verifyEmailOtpAndRegister({
                email: form.adminEmail.trim().toLowerCase(),
                code,
                fullName: form.fullName.trim(),
                username: form.username.trim(),
                password: form.password,
                facilityName: form.facilityName.trim(),
                facilityType: form.facilityType as ClinicFacilityType,
                businessPhoneE164: form.workspacePhone.trim(),
                licenseNumber: form.licenseNumber.trim(),
                workspaceEmail: form.workspaceEmail.trim(),
                physicalAddress: form.physicalAddress.trim(),
                country: form.country.trim().toUpperCase(),
                state: form.state,
                lga: form.lga,
                deviceUuid: getOrCreateDeviceUuid(),
            });

            // Flip auth state; ClinicGate + RoleLandingRedirect drop the
            // admin on the admin landing tab.
            signIn({
                accessToken: res.access_token,
                refreshToken: res.refresh_token,
                clinician: res.clinician,
            });
            onSuccess();
        } catch (err) {
            setError(registerFlowErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }

    async function resend() {
        setError(null);
        setResent(false);
        try {
            await authApi.requestEmailOtp(form.adminEmail.trim().toLowerCase());
            setResent(true);
        } catch (err) {
            setError(emailOtpRequestErrorMessage(err));
        }
    }

    return (
        <div className="mt-6">
            <p className="text-slate-600 text-sm">
                We sent a 6-digit code to <span className="font-medium text-slate-900">{form.adminEmail}</span>.
                Enter it to finish creating the workspace.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-4 max-w-sm">
                <div>
                    <label className="pm-label" htmlFor="otp">Verification Code</label>
                    <input
                        id="otp"
                        className="pm-input tracking-widest text-center text-lg"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="••••••"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        disabled={busy}
                    />
                </div>
                {error && <div className="pm-error">{error}</div>}
                {resent && !error && (
                    <div className="text-emerald-700 text-sm">A new code has been sent.</div>
                )}
                <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                    {busy ? 'Finalizing workspace…' : 'Verify & create workspace'}
                </button>
                <div className="flex items-center justify-between text-sm">
                    <button
                        type="button"
                        className="text-slate-600 hover:underline"
                        onClick={onBack}
                        disabled={busy}
                    >
                        ← Change details
                    </button>
                    <button
                        type="button"
                        className="text-brand-700 hover:underline"
                        onClick={resend}
                        disabled={busy}
                    >
                        Resend code
                    </button>
                </div>
            </form>
        </div>
    );
}

// ─── Success pane ──────────────────────────────────────────────────────

function SuccessPane() {
    // We only render this briefly — the ClinicGate re-render should replace
    // this screen with the app shell as soon as signIn() persists tokens.
    return (
        <div className="mt-8 text-center">
            <div className="text-4xl">🎉</div>
            <h2 className="text-slate-900 text-xl font-semibold mt-3">
                Workspace created
            </h2>
            <p className="text-slate-600 text-sm mt-2 max-w-sm mx-auto">
                Taking you into your workspace… You can also sign in on the
                Pamosi Clinic mobile app with the same phone number.
            </p>
        </div>
    );
}

// ─── Small pieces ──────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
    return (
        <div className="border-b border-slate-200 pb-3">
            <h2 className="text-slate-900 text-lg font-semibold flex items-center gap-2">
                <span aria-hidden>{icon}</span>
                {title}
            </h2>
            <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
        </div>
    );
}

function Req() {
    return <span className="text-rose-600" aria-label="required">*</span>;
}

function Hint({ children }: { children: React.ReactNode }) {
    return <div className="text-slate-500 text-xs mt-1">{children}</div>;
}

// ─── Validation ────────────────────────────────────────────────────────

function validate(f: FormState): string | null {
    if (!f.facilityType) return 'Please select a facility type.';
    if (!f.facilityName.trim()) return 'Workspace name is required.';
    if (!EMAIL_RE.test(f.workspaceEmail.trim())) return 'Workspace email is not valid.';
    if (!PHONE_RE.test(f.workspacePhone.trim())) {
        return 'Workspace phone must be in international format (e.g. +2348012345678).';
    }
    if (!f.physicalAddress.trim()) return 'Physical address is required.';
    if (!f.country) return 'Select a country.';
    if (!f.state) return 'Select a state.';
    if (!f.lga) return 'Select an LGA.';
    if (!f.licenseNumber.trim()) return 'License number is required.';
    if (!f.fullName.trim()) return 'Admin full name is required.';
    if (!EMAIL_RE.test(f.adminEmail.trim())) return 'Admin email is not valid.';
    if (!/^[a-zA-Z0-9._+@\-]{3,30}$/.test(f.username.trim())) {
        return 'Username must be 3–30 chars (letters, digits, . _ - + @).';
    }
    if (f.password.length < 8 || !/[a-zA-Z]/.test(f.password) || !/\d/.test(f.password)) {
        return 'Password must be 8+ characters with a letter and a number.';
    }
    if (f.password !== f.confirmPassword) return 'Passwords do not match.';
    return null;
}

function registerFlowErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
        switch (err.code) {
            case 'invalid_code':
                return 'That verification code is wrong or expired.';
            case 'expired_code':
                return 'That code has expired. Click "Resend code" to get a new one.';
            case 'too_many_attempts':
                return 'Too many attempts. Request a new code.';
            case 'username_taken':
                return 'That username is already taken. Try another.';
            case 'invalid_username':
                return 'Username can be 3–64 chars: letters, digits, and . _ - + @';
            case 'weak_password':
                return 'Password too weak. Use 8+ chars, a letter and a number.';
            case 'already_in_clinic':
                return 'This phone is already the admin of a workspace. Sign in instead.';
            case 'phone_already_registered':
                return 'This phone already has an account. Sign in instead.';
            case 'invalid_workspace_email':
                return 'Workspace email is not valid.';
            case 'invalid_email':
                return 'Admin email is not valid.';
            case 'invalid_business_phone':
                return 'Workspace phone is not valid.';
        }
    }
    return 'Could not create workspace. Try again.';
}

function emailOtpRequestErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
        switch (err.code) {
            case 'invalid_email':
                return 'That admin email is not valid.';
            case 'too_many_requests':
                return 'Too many requests. Try again in a minute.';
            case 'mail_not_configured':
                return 'Email delivery is not configured. Contact support.';
            case 'mail_provider_blocked':
                return 'Email provider rejected the request. Contact support.';
            case 'mail_provider_unavailable':
                return 'Email service is temporarily unavailable. Try again shortly.';
        }
    }
    return 'Could not send verification code. Try again.';
}
